import * as React from "react"
import { useTranslation } from "react-i18next"
import {
  Badge,
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui"
import {
  humanizePersonaIndustry,
  humanizePersonaTitle,
  humanizePersonaSentence,
} from "../../lib/persona-format"
import { PersonaStatusBadge } from "../PersonaStatusBadge/PersonaStatusBadge"
import { PersonaTypeBadge } from "../PersonaTypeBadge/PersonaTypeBadge"
import { PersonaKpi } from "../PersonaKpi/PersonaKpi"
import { PersonaPortrait } from "../PersonaPortrait/PersonaPortrait"
import { ImagePreviewTrigger } from "@/features/identity-hub/components/dashboard/ImagePreviewTrigger"
import type { Persona } from "../../types/personas.types"

/** Shared across every card variant (ready, skeleton, generating, error) so swapping between
 * them in a grid never reflows neighboring cards. Sized for the ready card's worst case
 * (two-line title + badge row + a three-line Who and two two-line profile rows) with
 * headroom for font-rendering variance. */
const PERSONA_CARD_HEIGHT_CLASS = "h-[400px]"

const GENERATING_DOT_DELAYS = ["-0.3s", "-0.15s", "0s"] as const

export interface PersonaCardProps {
  persona: Persona
  /** Denominator for the Share-of-Spend KPI — total tracked spend across the tenant's personas. */
  totalSpend: number
  /** Stagger for the Share-of-Spend meter's first-paint grow-in, set by `PersonaGrid`. */
  spendFillDelayMs?: number
  /** Opens the create-set dialog — omit to hide the CTA (e.g. no generate permission). */
  onCreateOpportunitySet?: (persona: Persona) => void
  /** Opens the detail drawer (U5) — the whole card is the click target except the CTA button. */
  onOpenDetail?: (persona: Persona) => void
}

const PROFILE_ROW_CLAMP_CLASS = {
  2: "line-clamp-2",
  3: "line-clamp-3",
} as const

function ProfileRow({
  label,
  value,
  lines = 2,
}: {
  label: string
  value: string | null
  /** Who is the persona's primary descriptor, so it gets the extra line. */
  lines?: keyof typeof PROFILE_ROW_CLAMP_CLASS
}) {
  if (!value) return null
  return (
    <div className="flex items-start gap-3 text-xs">
      <span className="w-20 shrink-0 pt-px font-medium text-muted-foreground">{label}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* line-clamp bounds row height so cards stay level across a grid row; full text stays available on hover. */}
          <span
            className={cn(
              "min-w-0 flex-1 cursor-default leading-snug text-foreground",
              PROFILE_ROW_CLAMP_CLASS[lines],
            )}
          >
            {value}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {value}
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export function PersonaCard({
  persona,
  totalSpend,
  spendFillDelayMs,
  onCreateOpportunitySet,
  onOpenDetail,
}: PersonaCardProps) {
  const { t } = useTranslation()
  const displayTitle = humanizePersonaTitle(persona.title)
  const mainPain = persona.pains[0] ? humanizePersonaSentence(persona.pains[0]) : null
  const mainMotivation = persona.motivations[0]
    ? humanizePersonaSentence(persona.motivations[0])
    : null
  // Rejected personas are read-only in the library: no drawer, no set generation.
  const isRejected = persona.status === "REJECTED"
  const isInteractive = Boolean(onOpenDetail) && !isRejected

  return (
    <div
      data-testid="persona-card"
      data-status={persona.status}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? () => onOpenDetail!(persona) : undefined}
      onKeyDown={
        isInteractive
          ? (e) => {
              // Ignore key events bubbled from nested interactive elements (e.g. the CTA
              // button) so their own native Space/Enter activation isn't hijacked.
              if (e.target !== e.currentTarget) return
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                onOpenDetail!(persona)
              }
            }
          : undefined
      }
      className={cn(
        "flex w-full flex-col gap-3 overflow-hidden rounded-md border border-border/60 bg-card p-4",
        PERSONA_CARD_HEIGHT_CLASS,
        // DESIGN.md allows a single shadow tier, so hover lifts the border rather than the elevation.
        "shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] transition-colors duration-150 ease-out",
        isInteractive && [
          "cursor-pointer hover:border-primary/25",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        ],
        isRejected && "opacity-55",
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <ImagePreviewTrigger
          src={persona.avatarUrl}
          alt={displayTitle}
          className="group/avatar rounded-full"
        >
          <span className="relative block transition-transform duration-150 ease-out motion-safe:group-hover/avatar:scale-105">
            <PersonaPortrait
              avatarUrl={persona.avatarUrl}
              personalityKey={persona.personalityKey}
              title={displayTitle}
              className="ring-1 ring-border transition-shadow duration-150 ease-out group-hover/avatar:ring-primary/50 motion-reduce:transition-none"
            />
            {persona.avatarUrl && (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-foreground/50 opacity-0 transition-opacity duration-150 ease-out group-hover/avatar:opacity-100 motion-reduce:transition-none"
              >
                <Icon name="maximize-2" size="xs" className="text-background" />
              </span>
            )}
          </span>
        </ImagePreviewTrigger>
        <div className="min-w-0 flex-1">
          {/* Clamped rather than truncated — the title is the persona's identity, so a long one
              wraps to a second line instead of losing its last words to an ellipsis. */}
          <p className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">{displayTitle}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <PersonaStatusBadge status={persona.status} />
            <PersonaTypeBadge type={persona.type} isMainBrand={persona.isMainBrand} />
            {persona.industry && <Badge variant="slate">{humanizePersonaIndustry(persona.industry)}</Badge>}
          </div>
        </div>
      </div>

      <PersonaKpi
        spend={persona.spend}
        totalSpend={totalSpend}
        adCount={persona.adCount}
        fillDelayMs={spendFillDelayMs}
      />

      {/* Profile */}
      <div className="flex flex-col gap-1.5 border-t border-border/50 pt-3">
        <ProfileRow label={t("personas.card.who")} value={persona.who} lines={3} />
        <ProfileRow label={t("personas.card.mainPain")} value={mainPain} />
        <ProfileRow label={t("personas.card.motivation")} value={mainMotivation} />
      </div>

      {/* mt-auto pins the CTA to the bottom regardless of how much profile content renders
          above, so the card's footprint never shifts (see PERSONA_CARD_HEIGHT_CLASS). */}
      {!isRejected && onCreateOpportunitySet && (
        <Button
          variant="outline"
          size="sm"
          className="mt-auto w-full"
          iconStart="plus"
          onClick={(e) => {
            e.stopPropagation()
            onCreateOpportunitySet(persona)
          }}
        >
          {t("personas.card.createOpportunitySet")}
        </Button>
      )}
    </div>
  )
}

export function PersonaCardSkeleton() {
  return (
    <div
      data-testid="persona-card-skeleton"
      className={cn(
        "relative flex w-full flex-col gap-3 overflow-hidden rounded-md border border-border/60 bg-card p-4",
        PERSONA_CARD_HEIGHT_CLASS,
      )}
    >
      {/* Shimmer sweep — matches OpportunityCardSkeleton's loading treatment for a consistent card family. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-md motion-reduce:hidden" aria-hidden>
        <div
          className="absolute inset-y-0 -left-1/2 w-1/2 animate-[osc-shimmer-sweep_1.6s_ease-in-out_infinite]"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, color-mix(in oklch, var(--foreground) 4%, transparent) 40%, color-mix(in oklch, var(--foreground) 7%, transparent) 50%, color-mix(in oklch, var(--foreground) 4%, transparent) 60%, transparent 100%)",
          }}
        />
      </div>

      <div className="relative flex items-start gap-3">
        <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
        <div className="min-w-0 flex-1">
          <div className="h-4 w-3/5 animate-pulse rounded bg-muted" />
          <div className="mt-1.5 h-4 w-1/3 animate-pulse rounded bg-muted" />
        </div>
      </div>
      <div className="relative h-[74px] animate-pulse rounded-md bg-muted/60" />
      <div className="relative flex flex-col gap-1.5 border-t border-border/50 pt-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-3.5 w-4/5 animate-pulse rounded bg-muted" />
        ))}
      </div>
      <div className="relative mt-auto h-8 animate-pulse rounded-md bg-muted" />
    </div>
  )
}

export interface PersonaCardGeneratingProps {
  /** Overrides the default "Generating persona…" copy — e.g. to show progress like "2 of 3". */
  label?: string
}

/** Shown for a persona an AI discover run is still producing. Mint-tinted (not primary blue) to
 * match DESIGN.md's AI-generated-content language, rather than the user-committed-action
 * blue used elsewhere (e.g. OpportunitySetCard's generating state). */
export function PersonaCardGenerating({ label }: PersonaCardGeneratingProps) {
  const { t } = useTranslation()
  const resolvedLabel = label ?? t("personas.card.generating", "Generating persona…")

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={resolvedLabel}
      data-testid="persona-card-generating"
      className={cn(
        "relative flex w-full flex-col overflow-hidden rounded-md border border-[var(--ai)]/20 bg-card p-4 shadow-none",
        PERSONA_CARD_HEIGHT_CLASS,
      )}
    >
      {/* Mesh + shimmer overlays */}
      <div
        className="pointer-events-none absolute inset-0 rounded-md motion-safe:animate-[osc-mesh-flow_4s_ease-in-out_infinite] motion-reduce:opacity-50"
        style={{
          background:
            "linear-gradient(135deg, color-mix(in oklch, var(--ai) 12%, transparent) 0%, color-mix(in oklch, var(--ai) 8%, transparent) 25%, color-mix(in oklch, var(--ai) 5%, transparent) 50%, color-mix(in oklch, var(--ai) 10%, transparent) 75%, color-mix(in oklch, var(--ai) 6%, transparent) 100%)",
          backgroundSize: "300% 300%",
        }}
      />
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-md motion-reduce:hidden" aria-hidden>
        <div
          className="absolute inset-y-0 -left-1/2 w-1/2 animate-[osc-shimmer-sweep_3s_ease-in-out_infinite]"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, color-mix(in oklch, var(--ai) 8%, transparent) 40%, color-mix(in oklch, var(--ai) 14%, transparent) 50%, color-mix(in oklch, var(--ai) 8%, transparent) 60%, transparent 100%)",
          }}
        />
      </div>

      {/* Content silhouette — hints at the real card's shape underneath the shimmer */}
      <div className="relative flex flex-1 select-none flex-col gap-3 motion-safe:animate-[osc-content-materialize_3s_ease-in-out_infinite] motion-reduce:opacity-30 motion-reduce:blur-sm">
        <div className="flex items-start gap-3">
          <div className="size-10 shrink-0 rounded-full bg-muted" />
          <div className="min-w-0 flex-1">
            <div className="h-4 w-3/5 rounded bg-muted" />
            <div className="mt-1.5 h-4 w-1/3 rounded bg-muted" />
          </div>
        </div>
        <div className="h-[74px] rounded-md bg-muted/60" />
        <div className="flex flex-col gap-1.5 border-t border-border/50 pt-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-3.5 w-4/5 rounded bg-muted" />
          ))}
        </div>
        <div className="mt-auto h-8 rounded-md bg-muted" />
      </div>

      {/* Generating label pill */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="flex items-center gap-2.5 rounded-full bg-card/90 px-4 py-2 shadow-sm ring-1 ring-[var(--ai)]/25 backdrop-blur-sm">
          <div className="flex items-end gap-1 motion-reduce:hidden" aria-hidden>
            {GENERATING_DOT_DELAYS.map((delay) => (
              <span
                key={delay}
                className="h-1.5 w-1.5 rounded-full bg-[var(--ai-deep)] motion-safe:animate-bounce"
                style={{ animationDelay: delay, animationDuration: "0.9s" }}
              />
            ))}
          </div>
          <Icon name="sparkles" size="xs" className="shrink-0 text-[var(--ai-text)]" />
          <span className="text-xs font-semibold text-[var(--ai-text)]">{resolvedLabel}</span>
        </div>
      </div>
    </div>
  )
}

export interface PersonaCardErrorProps {
  /** Persona title, if known before the failure (e.g. a retry of a previously-named persona). */
  title?: string
  /** Backend-provided error message — shown in full since it's the primary content, not truncated. */
  message?: string
  onRetry?: () => void
}

export function PersonaCardError({ title, message, onRetry }: PersonaCardErrorProps) {
  const { t } = useTranslation()

  return (
    <div
      role="alert"
      data-testid="persona-card-error"
      className={cn(
        "flex w-full flex-col overflow-hidden rounded-md border border-destructive/25 bg-destructive/[0.025] p-4",
        PERSONA_CARD_HEIGHT_CLASS,
        "motion-safe:animate-[error-shake_0.5s_ease-out] motion-reduce:animate-none",
      )}
    >
      <div className="flex items-start gap-2">
        <Icon
          name="alert-circle"
          size="sm"
          color="destructive"
          className="mt-0.5 shrink-0 motion-safe:animate-[error-icon-pulse_2.5s_ease-in-out_infinite] motion-reduce:opacity-80"
        />
        <span className="min-w-0 truncate text-sm font-semibold text-foreground">
          {title ?? t("personas.card.generationFailedTitle", "Persona generation failed")}
        </span>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-2 text-center">
        <p className="line-clamp-3 max-w-[85%] text-xs leading-relaxed text-destructive/75">
          {message ?? t("personas.card.generationFailed", "Couldn't generate this persona.")}
        </p>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            iconStart="refresh-cw"
            className="border-destructive/20 text-destructive/80 hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
            onClick={onRetry}
          >
            {t("common.retry", "Retry")}
          </Button>
        )}
      </div>
    </div>
  )
}
